<!-- MinLivsstilResults.vue - Fixed for iOS & Desktop Layout -->
<script setup lang="ts">
import LifestyleResult from '@/components/results/LifestyleResult.vue';
import { computed, inject, ref, onMounted, watch, onUnmounted } from 'vue';
import { useStore } from '@/stores/main';
import { useIonRouter } from '@ionic/vue';
import BIconButton from '@/components/BIconButton.vue';
import ArrowDown from '@/components/icons/ArrowDown.vue';

const props = defineProps<{
  sections: any[];
}>();

// Helper functions for status display
function getStatusColor(status: string | null | undefined) {
  if (!status) return 'bg-gray-300';
  
  if (status === 'profile.healthy' || status === 'healthy') {
    return 'bg-green-400';
  } else if (status === 'profile.risk' || status === 'risk') {
    return 'bg-red-400';
  } else {
    return 'bg-gray-300';
  }
}

function getStatusText(status: string | null | undefined) {
  if (!status) return 'Inte besvarat';
  
  if (status === 'profile.healthy' || status === 'healthy') {
    return 'Frisk';
  } else if (status === 'profile.risk' || status === 'risk') {
    return 'Risk';
  } else {
    return 'Obesvarad';
  }
}

const store = useStore();
const router = useIonRouter();
const colorScheme = inject('colorScheme', 'green');
const previousSlide = inject('previousSlide');

// UI state with improved defaults for iOS
const isLoaded = ref(false);
const activeTab = ref(0);
const isMobileView = ref(false);
const isIOSDevice = ref(false);
const isChartExpanded = ref(true); // Start with chart expanded by default

// Background color based on theme
const backgroundColor = computed(() => {
  return {
    purple: 'bg-purple-background',
    yellow: 'bg-yellow-background',
    green: 'bg-green-background',
    blue: 'bg-blue-background',
  }[colorScheme];
});

// Navigation function
async function next() {
  router.replace('/goals');
}

// Scroll handling
const scrollArea = ref<HTMLElement | null>(null);
const showScrollButton = ref(false);

function scroll() {
  if (scrollArea.value === null) {
    return;
  }
  scrollArea.value.scrollBy({ top: 150, behavior: 'smooth' });
}

// Adjust chart size for mobile/desktop
function toggleChartSize() {
  isChartExpanded.value = !isChartExpanded.value;
  
  // Scroll to top when expanding
  if (isChartExpanded.value && scrollArea.value) {
    scrollArea.value.scrollTo({ top: 0, behavior: 'smooth' });
  }
}

// Detect iOS devices
function detectIOSDevice() {
  const userAgent = window.navigator.userAgent.toLowerCase();
  isIOSDevice.value = /iphone|ipad|ipod|macintosh/.test(userAgent) && 'ontouchend' in document;
  
  // On iOS, force chart to be visible initially
  if (isIOSDevice.value) {
    isChartExpanded.value = true;
  }
}

// Handle window resize with improved detection
function handleResize() {
  isMobileView.value = window.innerWidth < 768;
  
  // On desktop, always show expanded chart
  if (!isMobileView.value) {
    isChartExpanded.value = true;
  }
  
  // Update scroll button visibility
  updateScrollButtonVisibility();
}

// Update scroll button visibility
function updateScrollButtonVisibility() {
  if (scrollArea.value) {
    showScrollButton.value = scrollArea.value.scrollHeight > scrollArea.value.clientHeight + 50; // Add buffer
  }
}

// Setup on component mount
onMounted(() => {
  // Detect device type
  detectIOSDevice();
  
  // Initialize responsive state
  handleResize();
  
  // Add event listeners
  window.addEventListener('resize', handleResize);
  
  // Show content
  setTimeout(() => {
    isLoaded.value = true;
    
    // Force layout recalculation after loading
    setTimeout(() => {
      updateScrollButtonVisibility();
    }, 200);
  }, 100);
  
  // Set up scroll observation
  if (scrollArea.value) {
    const observer = new ResizeObserver(() => {
      updateScrollButtonVisibility();
    });
    observer.observe(scrollArea.value);
    
    // Setup cleanup
    onUnmounted(() => {
      window.removeEventListener('resize', handleResize);
      observer.disconnect();
    });
  }
});

// Watch for tab changes to update scroll button
watch(activeTab, updateScrollButtonVisibility);
</script>

<template>
  <div
    :class="[backgroundColor, { 'opacity-0': !isLoaded, 'opacity-100': isLoaded }]"
    class="w-full flex flex-col text-[#171717]  relative  transition-opacity duration-300"
  >
    <!-- Main content section -->
    <div
      
      class="w-full flex !flex-col px-4 relative  sm:px-6 pt-4  sm:pt-8 pb-96  overflow-y-auto overflow-x-hidden max-h-screen !h-auto  "
     
    >
      <!-- Responsive Header -->
      <div class="flex items-center  justify-between ">
        <h2 class="font-light text-xl sm:text-2xl">Bra jobbat, se resultatet!</h2>
        
        <!-- Mobile toggle button -->
        <button 
          v-if="isMobileView" 
          @click="toggleChartSize"
          class="text-sm px-3 py-1 rounded-full bg-white/70 text-gray-700 shadow-sm flex items-center gap-1"
        >
          <span v-if="isChartExpanded">Visa detaljer</span>
          <span v-else>Visa graf</span>
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" :class="isChartExpanded ? 'rotate-180' : ''">
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </button>
      </div>
      
      <!-- Responsive Layout with Conditional Chart Display -->
      <div class="desktop-container items-center gap-12   justify-center !w-full !flex-col">
        <!-- Chart Section - Always visible on load, toggle on mobile -->
        <div 
          class="chart-container !w-full "
          :class="{
            'hidden-on-mobile': !isChartExpanded && isMobileView,
            'visible-on-mobile': isChartExpanded || !isMobileView,
            'ios-chart-fix': isIOSDevice
          }"
        >
          <div class="chart-wrapper !mx-auto">
            <h2 class="font-normal text-lg">Klicka på tårtbitarna</h2>
            <LifestyleResult
              :sections="props?.sections || []"
              :factors="store.profile?.factors"
            />
          </div>
        </div>
        
        <!-- Tabs Section - Conditionally shown based on mobile toggle -->
        <div 
          class="details-container  md:mt-0 !w-full "
          :class="{
            'hidden-on-mobile': isChartExpanded && isMobileView,
            'visible-on-mobile': !isChartExpanded || !isMobileView
          }"
        >
          <div class="text-sm text-black/60 mb-2 sm:mb-4">
            Välj ett område för att se detaljerad information:
          </div>
          
          <!-- Responsive Tab Headers -->
          <div class="flex flex-wrap md:flex-nowrap overflow-x-auto gap-2 pb-2 mb-4 no-scrollbar">
            <button 
              v-for="(section, index) in props.sections" 
              :key="index"
              @click="activeTab = index"
              class="py-1 sm:py-2 px-3 sm:px-4 rounded-full whitespace-nowrap text-sm sm:text-base transition-colors duration-200 mb-1"
              :class="activeTab === index ? 
                'bg-green-darker text-white shadow-md' : 
                'bg-white/70 text-black/70 hover:bg-white/90'"
            >
              {{ section.pageLabel }}
            </button>
          </div>
          
          <!-- Tab Content -->
          <div v-if="props.sections.length > 0" class="bg-white/90 rounded-lg p-3 sm:p-4 shadow-md">
            <template v-for="(section, index) in props.sections" :key="index">
              <div v-if="activeTab === index" class="animate-fadeIn">
                <h3 class="font-medium text-base sm:text-lg mb-2">{{ section.pageLabel }}</h3>
                
                <div v-if="section.groups && section.groups.length > 0" class="space-y-3">
                  <!-- Display factors for this section -->
                  <div 
                    v-for="(group, groupIndex) in section.groups" 
                    :key="groupIndex"
                    v-show="group.category_id && store.profile?.factors?.[group.category_id]"
                  >
                    <div class="p-2 sm:p-3 bg-white/70 rounded border border-gray-200 hover:bg-white/90 transition-colors">
                      <div class="font-medium text-sm sm:text-base">{{ group.label }}</div>
                      <div 
                        v-if="group.category_id && store.profile?.factors?.[group.category_id]" 
                        class=" flex items-center justify-center mt-3"
                      >
                        <div 
                          class="w-16 sm:w-20 h-2  sm:h-3 rounded-full bg-gray-200 overflow-hidden mr-2"
                          v-if="store.profile?.factors?.[group.category_id]?.value"
                        >
                          <div 
                            class="h-full rounded-full" 
                            :class="getStatusColor(store.profile?.factors?.[group.category_id]?.status)"
                           
                          ></div>
                        </div>
                        <span class="text-xs sm:text-sm">
                          {{ getStatusText(store.profile?.factors?.[group.category_id]?.status) }}
                       
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div v-else class="text-center py-4 text-gray-500 text-sm sm:text-base">
                  Ingen information tillgänglig för detta område
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
      
      <!-- Mobile View: Toggle Button When Scrolled -->
      <div 
        v-if="isMobileView" 
        class="toggle-button-container"
      >
        <button 
          @click="toggleChartSize"
          class="toggle-button"
        >
          <span v-if="isChartExpanded">Visa detaljer</span>
          <span v-else>Visa graf</span>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" :class="isChartExpanded ? 'rotate-180' : ''">
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </button>
      </div>

     
    </div>
   
    <!-- Scroll Button -->
    <div 
      v-if="showScrollButton" 
      class="scroll-button-container"
    >
      <b-icon-button @click="scroll" class="shadow-md">
        <arrow-down />
      </b-icon-button>
    </div>
    
    <!-- Fixed navigation bar - improved for iOS -->
    <!-- <div
      class=" mt-12  "
      :class="{'ios-nav-fix': isIOSDevice}"
    >
      <div class="navigation-buttons">
        <b-button @click="previousSlide" look="secondary">Tillbaka</b-button>
        <b-button @click="next">Nästa</b-button>
      </div>
    </div> -->
  </div>
</template>

<style scoped lang="postcss">
/* Added iOS safe area inset support */
.safe-area-inset-bottom {
  padding-bottom: calc(1rem + env(safe-area-inset-bottom, 0));
}

/* Layout structure */
.desktop-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 100%;
}

.chart-container {
  transition: all 0.3s ease-in-out;
  margin-bottom: 1.5rem;
}

.chart-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2.4rem 0.5rem 1rem;
  background-color: rgba(255, 255, 255, 0.5);
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.details-container {
  transition: all 0.3s ease-in-out;
}

/* Mobile visibility classes */
.hidden-on-mobile {
  height: 0;
  overflow: hidden;
  margin: 0;
  padding: 0;
  opacity: 0;
}

.visible-on-mobile {
  height: auto;
  opacity: 1;
}

/* Toggle button */
.toggle-button-container {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 20;
  margin-top: 1rem;
}

.toggle-button {
  background-color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  padding: 0.5rem 1rem;
  border-radius: 9999px;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Scroll button */
.scroll-button-container {
  position: fixed;
  bottom: 24px;
  right: 1rem;
  z-index: 20;
}

/* Navigation bar */
.navigation-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  background-color: #f9fcf2;
  border-top: 1px solid rgba(0, 0, 0, 0.2);
  padding: 0.75rem 1rem;
  z-index: 30;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  padding-bottom: calc(1rem + env(safe-area-inset-bottom, 0));
}

.navigation-buttons {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
  max-width: 20rem;
  margin: 0 auto;
}

/* iOS specific fixes */
.ios-scroll-fix {
  -webkit-overflow-scrolling: touch;
  position: relative;
}

.ios-chart-fix {
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
}

.ios-nav-fix {
  padding-bottom: calc(1.5rem + env(safe-area-inset-bottom, 20px));
}

@supports (-webkit-touch-callout: none) {
  /* iOS specific - ensure buttons are not hidden under home indicator */
  .pb-24 {
    padding-bottom: calc(6rem + env(safe-area-inset-bottom, 0));
  }
  
  .scroll-button-container,
  .toggle-button-container {
    bottom: calc(24px + env(safe-area-inset-bottom, 0));
  }
}

/* Hide scrollbar but allow scrolling */
.no-scrollbar {
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
}

.no-scrollbar::-webkit-scrollbar {
  display: none;  /* Chrome, Safari and Opera */
}

/* Simple fade-in animation */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.animate-fadeIn {
  animation: fadeIn 0.3s ease-in-out;
}

/* Desktop layout */
@media (min-width: 1024px) {
  .desktop-container {
    flex-direction: row;
    align-items: flex-start;
  }
  
  .chart-container {
    width: 50%;
    margin-bottom: 0;
  }
  
  .details-container {
    width: 50%;
  }
  
  .chart-wrapper {
    height: 100%;
    justify-content: center;
  }
}

/* Tablet layout */
@media (min-width: 768px) and (max-width: 1023px) {
  .desktop-container {
    flex-direction: column;
  }
  
  .chart-container, 
  .details-container {
    width: 100%;
  }
}

/* Small screen optimizations */
@media (max-width: 320px) and (max-height: 568px) {
  .navigation-bar {
    padding: 0.5rem 0.75rem;
    padding-bottom: calc(0.5rem + env(safe-area-inset-bottom, 0));
  }
  
  h2 {
    font-size: 1.2rem;
  }
  
  button {
    padding-top: 0.375rem;
    padding-bottom: 0.375rem;
  }
  
  .toggle-button-container,
  .scroll-button-container {
    bottom: 18px;
  }
}

/* Landscape mode */
@media (orientation: landscape) and (max-height: 500px) {
  .min-h-[70vh], .max-h-[70vh] {
    min-height: 85vh;
    max-height: 85vh;
  }
  
  .toggle-button-container {
    bottom: 16px;
  }
  
  .scroll-button-container {
    bottom: 16px;
  }
  
  .pb-24 {
    padding-bottom: 4rem;
  }
}

/* Print styles */
@media print {
  .fixed {
    display: none !important;
  }
  
  .min-h-[70vh], .max-h-[70vh], .min-h-[76vh], .max-h-[76vh] {
    min-height: auto;
    max-height: none;
    overflow: visible;
  }
  
  .animate-fadeIn {
    animation: none;
  }
  
  .overflow-hidden, .overflow-y-auto {
    overflow: visible;
  }
  
  .mt-[130px] {
    margin-top: 0;
  }
  
  .desktop-container {
    flex-direction: column;
  }
  
  .chart-container,
  .details-container {
    width: 100%;
    height: auto;
    opacity: 1;
    overflow: visible;
  }
}
</style>